import type { Dispatch } from 'react';
import { createElement, useCallback } from 'react';
import { useMutation, type ApolloError } from '@apollo/client';
import type {
  CreateAddendumMutation,
  CreateAddendumMutationVariables,
  DeleteAddendumMutation,
  DeleteAddendumMutationVariables,
  CancelAddendumMutation,
  CancelAddendumMutationVariables,
  SubmitAddendumMutation,
  SubmitAddendumMutationVariables,
} from 'generated/graphql';
import analytics from 'modules/analytics';
import { reporter } from 'modules/analytics/constants';
import { GET_WORKLIST_ITEM } from 'modules/Apollo/queries';
import { CrossWindowDataRefetchType, NameSpaces, sendCrossTabEvent } from 'modules/EventsManager';
import { broadcastCaseAndWorklistRefresh } from 'common/CurrentCase/broadcastCaseAndWorklistRefresh';
import { logger } from 'modules/logger';
import { ReportExitAction } from 'domains/reporter/RichTextEditor/analytics/performance';
import { ReporterSaveState } from 'config/recoilState';
import type { EnqueueToastFn } from 'common/ui/Toaster';
import type { LoadedReportWorklistItem } from 'hooks/useCurrentCaseReport';
import type { HandleAutoloadNextCaseParams } from 'hooks/useWorklistAutoLoad';
import { getReportSMID, getCaseInfo, getDefaultSlateFieldChildren } from './utils';
import { ReportStatus } from '../state';
import type { ReportStatuses } from '../state';
import type { ActionType, Addendum } from './fieldsReducer';
import type { SlateContent } from '../types';
import { SubmitType, type OnSubmitParams } from './types';
import type { SubmissionTarget } from './useSubmissionWorkstationID';
import { findEnforcedEmptyRequiredFields } from './reportValidation';
import {
  enforceRequiredFields,
  notifySubmissionCancelled,
  notifyWorkstationPatchFailure,
} from './submitGuards';
import { useAutoSaveReport } from './useAutoSaveReport';
import { SironaSupportLink } from 'common/SironaSupportLink';
import { Colors } from 'styles';
import { getAddendumCreationErrorDisplay } from '../../utils/getAddendumCreationErrorDisplay';

type UseAutoSaveReportReturn = ReturnType<typeof useAutoSaveReport>;

const createAddendumCreationErrorMessage = (error: unknown) => {
  const display = getAddendumCreationErrorDisplay({ error });

  if (display.kind !== 'generic') {
    return `Unable to create the addendum due to ${display.reason}.`;
  }

  return createElement(
    'span',
    null,
    'Unable to create the addendum. Try again. If the problem persists, contact ',
    createElement(SironaSupportLink),
    '.'
  );
};

const createAddendumSignErrorMessage = () =>
  createElement(
    'span',
    null,
    'Unable to sign the report. Try again. If the problem persists, contact ',
    createElement(SironaSupportLink, { color: Colors.gray0 }),
    '.'
  );

/**
 * Single owner of the addendum lifecycle. This first slice (M4a) owns the standalone commands —
 * creating a new addendum and cancelling/discarding an in-progress one. Save is delegated to
 * useAutoSaveReport (addendumUpdateDebounced) and report-exit effects to reportExitActionEffects
 * (maybeAutoloadNextCaseCallback); the submit / draft / cancel-submit branches follow in M4b.
 */
export interface UseAddendumWorkflowParams {
  worklistItem: LoadedReportWorklistItem;
  addendumSMID: Nullable<string>;
  createAddendum: ReturnType<
    typeof useMutation<CreateAddendumMutation, CreateAddendumMutationVariables>
  >[0];
  deleteAddendum: ReturnType<
    typeof useMutation<DeleteAddendumMutation, DeleteAddendumMutationVariables>
  >[0];
  cancelAddendum: ReturnType<
    typeof useMutation<CancelAddendumMutation, CancelAddendumMutationVariables>
  >[0];
  dispatch: Dispatch<ActionType>;
  setStatus: (status: ReportStatuses) => void;
  cancelCountdown: () => void;
  setRightDrawerCatalogRefreshSequence: (updater: (previous: number) => number) => void;
  analyticsData: Record<string, unknown> | undefined;
  resetEditorWithContent: (content: SlateContent) => void;
  isRecording: boolean;
  restartRecording: () => void;
  refetchCurrentCase?: () => Promise<unknown>;
  broadcastRefreshCase: () => Promise<unknown>;
  enqueueToast: EnqueueToastFn;
  addendumUpdateDebounced: UseAutoSaveReportReturn['addendumUpdateDebounced'];
  maybeAutoloadNextCaseCallback: (
    isWorklistAutoLoadEnabled: boolean,
    defaultToastMsg: string,
    params: HandleAutoloadNextCaseParams
  ) => Promise<void>;
  isWorklistAutoLoadEnabled: boolean;
  // submitAddendum (M5b) dependencies
  addendum: Nullable<Addendum>;
  stopRecording: () => Promise<void>;
  setSavingState: UseAutoSaveReportReturn['setSavingState'];
  addendumUpdate: UseAutoSaveReportReturn['addendumUpdate'];
  submitAddendumMutation: ReturnType<
    typeof useMutation<SubmitAddendumMutation, SubmitAddendumMutationVariables>
  >[0];
  resolveSubmissionWorkstationID: (target: SubmissionTarget) => Promise<{
    cancelled: boolean;
    workstationIDToSubmit: string | null;
    requiresPatchBeforeSubmit: boolean;
  }>;
  trackTypedCharacters: () => void;
  startCountdown: () => void;
  isBypassAndSign: boolean;
  me: Nullable<{ firstName?: Nullable<string>; lastName?: Nullable<string> }>;
  isEmptyRequiredFieldsDialogOpen: boolean;
  openEmptyRequiredFieldsDialog: (
    emptyRequiredFields: ReturnType<typeof findEnforcedEmptyRequiredFields>
  ) => void;
  setAllRequiredFieldsTouched: () => void;
}

export const useAddendumWorkflow = ({
  worklistItem,
  addendumSMID,
  createAddendum,
  deleteAddendum,
  cancelAddendum,
  dispatch,
  setStatus,
  cancelCountdown,
  setRightDrawerCatalogRefreshSequence,
  analyticsData,
  resetEditorWithContent,
  isRecording,
  restartRecording,
  refetchCurrentCase,
  broadcastRefreshCase,
  enqueueToast,
  addendumUpdateDebounced,
  maybeAutoloadNextCaseCallback,
  isWorklistAutoLoadEnabled,
  addendum,
  stopRecording,
  setSavingState,
  addendumUpdate,
  submitAddendumMutation,
  resolveSubmissionWorkstationID,
  trackTypedCharacters,
  startCountdown,
  isBypassAndSign,
  me,
  isEmptyRequiredFieldsDialogOpen,
  openEmptyRequiredFieldsDialog,
  setAllRequiredFieldsTouched,
}: UseAddendumWorkflowParams) => {
  const handleAddendumAdd = useCallback(async () => {
    const reportSmid = getReportSMID(worklistItem);
    const { data } = await createAddendum({
      variables: {
        reportSmid,
      },
      update: (proxy, { data }) => {
        if (data == null) return;

        proxy.writeQuery({
          query: GET_WORKLIST_ITEM,
          data: {
            worklistItem: {
              ...worklistItem,
              report: {
                ...worklistItem.report,
                addendums: [...(worklistItem?.report?.addendums ?? []), data.createAddendum],
              },
            },
          },
        });
      },
      onError: (e) => {
        logger.error('[useAddendumWorkflow] Error occurred when creating addendum: ', e);

        enqueueToast(createAddendumCreationErrorMessage(e), {
          icon: 'redWarning',
        });
      },
    });

    if (data == null) return;

    try {
      if (refetchCurrentCase) {
        await refetchCurrentCase();
      }
    } catch (error) {
      logger.warn('[useAddendumWorkflow] Failed to refetch case after creating addendum', {
        addendumSmid: data.createAddendum.smid,
        error,
        reportSmid,
        worklistItemSmid: worklistItem.smid,
      });
    }

    dispatch({ type: 'addAddendum' });
    setStatus(ReportStatus.AddendumAdd);
    resetEditorWithContent(getDefaultSlateFieldChildren());

    // The editor content (and any in-flight dictation targeting it) was just wiped out from
    // under an active recording session — restart so dictation re-anchors to the new content
    // instead of continuing to target the old, now-gone selection.
    if (isRecording) {
      restartRecording();
    }

    if (typeof data?.createAddendum?.smid === 'string') {
      dispatch({ type: 'addAddendumSMID', payload: { addendumSMID: data.createAddendum.smid } });
    }

    // Emit the cross-tab event before the broadcast: the addendum is already created, so the
    // CLAIMED_ITEMS notification must still reach other tabs even if broadcastRefreshCase() rejects.
    // This create path keeps its own ordering rather than the broadcast-first shared helper.
    sendCrossTabEvent(NameSpaces.CROSS_WINDOW_DATA_REFETCH, {
      type: CrossWindowDataRefetchType.CLAIMED_ITEMS,
    });
    await broadcastRefreshCase();
  }, [
    createAddendum,
    worklistItem,
    setStatus,
    refetchCurrentCase,
    enqueueToast,
    resetEditorWithContent,
    isRecording,
    restartRecording,
    broadcastRefreshCase,
    dispatch,
  ]);

  const handleCancelAddAddendum = useCallback(
    async ({
      onSuccess,
      onError,
    }: {
      onSuccess?: () => void;
      onError?: () => void;
    } = {}) => {
      if (addendumSMID == null) return;

      addendumUpdateDebounced.cancel();

      dispatch({ type: 'clearAddAddendum' });
      setStatus(ReportStatus.Submitted);
      await deleteAddendum({
        variables: { smid: addendumSMID },
        awaitRefetchQueries: true,
        refetchQueries: [{ query: GET_WORKLIST_ITEM, variables: { smid: worklistItem.smid } }],
        update: (proxy, { data }) => {
          if (data == null || !data.deleteAddendum.confirmed) return;

          proxy.writeQuery({
            query: GET_WORKLIST_ITEM,
            data: {
              worklistItem: {
                ...worklistItem,
                report: {
                  ...worklistItem.report,
                  addendums:
                    worklistItem?.report?.addendums?.filter(({ smid }) => smid !== addendumSMID) ??
                    [],
                },
              },
            },
          });
        },
        onCompleted: async () => {
          const defaultToastMsg = `Addendum for ${getCaseInfo(worklistItem)} has been discarded.`;

          await broadcastCaseAndWorklistRefresh(
            broadcastRefreshCase,
            CrossWindowDataRefetchType.CLAIMED_ITEMS
          );

          await maybeAutoloadNextCaseCallback(isWorklistAutoLoadEnabled, defaultToastMsg, {
            reportExitAction: ReportExitAction.Discard,
            defaultToastMsg,
            isAddendum: true,
          });
          onSuccess && onSuccess();
        },
        onError: () => {
          onError && onError();
        },
      });
    },
    [
      addendumSMID,
      addendumUpdateDebounced,
      setStatus,
      deleteAddendum,
      worklistItem,
      broadcastRefreshCase,
      maybeAutoloadNextCaseCallback,
      isWorklistAutoLoadEnabled,
      dispatch,
    ]
  );

  const cancelAddendumSubmit = useCallback(async () => {
    cancelCountdown();
    if (addendumSMID == null) return;

    setStatus(ReportStatus.AddendumCancelling);
    analytics.track(reporter.usr.submitReportCancel, {
      ...analyticsData,
      smid: addendumSMID,
    });

    await cancelAddendum({
      variables: { smid: addendumSMID },
      update: (proxy, { data }) => {
        if (data == null) return;

        proxy.writeQuery({
          query: GET_WORKLIST_ITEM,
          data: {
            worklistItem: {
              ...worklistItem,
              report: {
                ...worklistItem.report,
                addendums: worklistItem?.report?.addendums?.map((addendum: any) => {
                  if (addendum.smid === data.cancelAddendum.smid) {
                    return { ...addendum, submittedAt: null };
                  }

                  return addendum;
                }),
              },
            },
          },
        });
      },
    });
    setRightDrawerCatalogRefreshSequence((previous) => previous + 1);
    setStatus(ReportStatus.AddendumAdd);
  }, [
    cancelCountdown,
    addendumSMID,
    setStatus,
    analyticsData,
    cancelAddendum,
    worklistItem,
    setRightDrawerCatalogRefreshSequence,
  ]);

  const submitAddendum = useCallback(
    async (options?: OnSubmitParams): Promise<void> => {
      const isPrelim = options?.type === SubmitType.Prelim;

      if (isRecording === true) {
        await stopRecording();
      }

      await addendumUpdateDebounced.flush();

      if (
        enforceRequiredFields({
          content: addendum?.content ?? null,
          isPrelim,
          isEmptyRequiredFieldsDialogOpen,
          openEmptyRequiredFieldsDialog,
          setAllRequiredFieldsTouched,
        })
      ) {
        options?.onError && options?.onError();
        return;
      }

      if (addendumSMID == null) {
        options?.onError && options?.onError();
        return;
      }

      const addendumWorkstationSubmission = await resolveSubmissionWorkstationID('addendum');

      if (addendumWorkstationSubmission.cancelled) {
        notifySubmissionCancelled({ onError: options?.onError });
        return;
      }

      if (addendumWorkstationSubmission.requiresPatchBeforeSubmit) {
        setSavingState(ReporterSaveState.Saving);
        let workstationPatchError: ApolloError | undefined;
        const didPatchWorkstationID = await addendumUpdate({
          contentToSubmit: addendum?.content,
          addendumSMID,
          workstationIDToSubmit: addendumWorkstationSubmission.workstationIDToSubmit,
          errorMode: 'submissionWorkstationPatch',
          onError: (error) => {
            workstationPatchError = error;
          },
        });

        if (didPatchWorkstationID !== true) {
          notifyWorkstationPatchFailure({
            enqueueToast,
            target: 'addendum',
            error: workstationPatchError,
            onError: options?.onError,
          });
          return;
        }
      }

      setStatus(ReportStatus.AddendumSubmitting);

      analytics.track(reporter.usr.submitAddendum, {
        ...analyticsData,
        smid: addendumSMID,
      });
      trackTypedCharacters();

      const { data: submitAddendumData } = await submitAddendumMutation({
        variables: { smid: addendumSMID, immediate_submit: isBypassAndSign },
        update: (proxy, { data }) => {
          if (data == null) return;
          const addendums = worklistItem?.report?.addendums || [];
          proxy.writeQuery({
            query: GET_WORKLIST_ITEM,
            data: {
              worklistItem: {
                ...worklistItem,
                report: {
                  ...worklistItem.report,
                  addendums: addendums.map((a) => {
                    if (a.smid === data.submitAddendum.smid) return data.submitAddendum;
                    return a;
                  }),
                },
              },
            },
          });
        },
        onError: (e) => {
          enqueueToast(createAddendumSignErrorMessage(), {
            icon: 'redWarning',
          });
          logger.error('[useAddendumWorkflow] Error occurred when submitting addendum: ', e);
          options?.onError && options?.onError();
        },
      });

      if (!submitAddendumData?.submitAddendum) {
        setStatus(ReportStatus.AddendumAdd);
        options?.onError && options?.onError();
        return;
      }

      if (isBypassAndSign) {
        setStatus(ReportStatus.Submitted);
        // immediately show timestamp and user
        dispatch({
          type: 'finalizeProvisionalAddendum',
          payload: {
            submittingUserFirstName: me?.firstName,
            submittingUserLastName: me?.lastName,
            addendumSubmittedDate: submitAddendumData?.submitAddendum?.submittedAt,
          },
        });
      } else {
        startCountdown();
        setStatus(ReportStatus.ProvisionalAddendumSubmit);
      }

      dispatch({
        type: 'submitAddendum',
        payload: { addendumSubmittedDate: submitAddendumData?.submitAddendum?.submittedAt },
      });

      const defaultToastMsg = `Report for ${getCaseInfo(worklistItem)} has been signed.`;

      await broadcastCaseAndWorklistRefresh(
        broadcastRefreshCase,
        CrossWindowDataRefetchType.CLAIMED_ITEMS
      );

      await maybeAutoloadNextCaseCallback(isWorklistAutoLoadEnabled, defaultToastMsg, {
        reportExitAction: isBypassAndSign ? ReportExitAction.BypassSign : ReportExitAction.Sign,
        defaultToastMsg,
        isAddendum: true,
      });
      setRightDrawerCatalogRefreshSequence((previous) => previous + 1);

      // If the user was closing the window, we want to close it now that the report has been signed
      options?.onSuccess && options.onSuccess();
    },
    [
      isRecording,
      stopRecording,
      addendumUpdateDebounced,
      addendum,
      isEmptyRequiredFieldsDialogOpen,
      openEmptyRequiredFieldsDialog,
      setAllRequiredFieldsTouched,
      addendumSMID,
      resolveSubmissionWorkstationID,
      enqueueToast,
      setSavingState,
      addendumUpdate,
      setStatus,
      analyticsData,
      trackTypedCharacters,
      submitAddendumMutation,
      isBypassAndSign,
      worklistItem,
      dispatch,
      me,
      startCountdown,
      broadcastRefreshCase,
      maybeAutoloadNextCaseCallback,
      isWorklistAutoLoadEnabled,
      setRightDrawerCatalogRefreshSequence,
    ]
  );

  return { handleAddendumAdd, handleCancelAddAddendum, cancelAddendumSubmit, submitAddendum };
};
